/**
 * Durable persistence for the templates-agent chat settings, so they survive a page reload / session
 * resume (the chat store's `chatInput.options` is in-memory only and is reset by `setAgentById`).
 *
 * Mirrors the `entities/workspace` store pattern: a single `localStorage` key holding a JSON map,
 * with safe read/parse helpers. Two kinds of entries are stored under that map:
 *   - `session:<chatId>`      → the settings used in that specific session (restored on resume)
 *   - `ws:<workspaceId>`      → the workspace's last-used settings (seed for a brand-new chat)
 *
 * Only the templates-agent settings subset is persisted (never draft text / files). `model` is kept
 * in its stored shape (`llmOptions.model`) so a restore is a plain merge back into `chatInput.options`.
 */

const STORAGE_KEY = 'mf_templates_chat_settings';

export interface PersistedTemplatesSettings {
  llmOptions?: { model?: string };
  selectedTemplate?: string;
  selectedCampaignId?: string;
  knowledgeBaseIds?: string[];
  selectedGuidelineIds?: string[];
  autoApplyQa?: boolean;
}

/** The `chatInput.options` keys we persist/restore. Everything else in the bag is ignored. */
export const PERSISTED_KEYS = [
  'llmOptions',
  'selectedTemplate',
  'selectedCampaignId',
  'knowledgeBaseIds',
  'selectedGuidelineIds',
  'autoApplyQa',
] as const;

/**
 * How many `session:<chatId>` entries to keep. A session entry is written for every chat ever opened
 * and is never otherwise removed, so without a cap the map would grow without bound. 150 is far above
 * anything a user realistically juggles (so a still-relevant session's settings are never evicted)
 * while keeping the stored blob comfortably small. `ws:<workspaceId>` entries are naturally bounded by
 * workspace count and are never evicted.
 */
const MAX_SESSION_ENTRIES = 150;

/** A stored entry: the settings plus the epoch-ms of the last write (used for LRU eviction). */
interface StoredEntry {
  settings: PersistedTemplatesSettings;
  updatedAt: number;
}

type SettingsMap = Record<string, StoredEntry>;

const sessionKey = (chatId: string): string => `session:${chatId}`;
const workspaceKey = (workspaceId: string): string => `ws:${workspaceId}`;
const isSessionKey = (key: string): boolean => key.startsWith('session:');

/** True for the current `{ settings, updatedAt }` entry shape (vs a legacy bare settings object). */
function isStoredEntry(value: unknown): value is StoredEntry {
  return (
    typeof value === 'object' &&
    value !== null &&
    'settings' in value &&
    typeof (value as StoredEntry).updatedAt === 'number'
  );
}

/**
 * Reads the whole map, tolerating absent/corrupt storage (returns `{}`). Legacy entries written before
 * the timestamp wrapper existed (a bare settings object) are migrated in-memory to `{ settings,
 * updatedAt: 0 }` so they read back correctly and sort as oldest for eviction.
 */
function readMap(): SettingsMap {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return {};
    const result: SettingsMap = {};
    for (const [key, value] of Object.entries(parsed as Record<string, unknown>)) {
      result[key] = isStoredEntry(value)
        ? value
        : { settings: value as PersistedTemplatesSettings, updatedAt: 0 };
    }
    return result;
  } catch {
    return {};
  }
}

/** Drops the oldest `session:` entries so at most {@link MAX_SESSION_ENTRIES} remain. Mutates `map`. */
function evictOldSessions(map: SettingsMap): void {
  const sessionKeys = Object.keys(map).filter(isSessionKey);
  if (sessionKeys.length <= MAX_SESSION_ENTRIES) return;
  // Oldest first; remove from the front until we're back under the cap.
  sessionKeys.sort((a, b) => map[a].updatedAt - map[b].updatedAt);
  for (const key of sessionKeys.slice(0, sessionKeys.length - MAX_SESSION_ENTRIES)) {
    Reflect.deleteProperty(map, key);
  }
}

function writeMap(map: SettingsMap): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    // Storage full / unavailable (private mode): persistence is best-effort, never throw.
  }
}

/** Extracts the persistable subset from the loose `chatInput.options` bag. */
export function pickPersistableSettings(
  options: Record<string, unknown> | undefined,
): PersistedTemplatesSettings {
  const result: PersistedTemplatesSettings = {};
  if (!options) return result;
  for (const key of PERSISTED_KEYS) {
    const value = options[key];
    if (value !== undefined) (result as Record<string, unknown>)[key] = value;
  }
  return result;
}

/** True when two settings snapshots are equivalent (avoids needless writes / restore loops). */
export function settingsEqual(
  a: PersistedTemplatesSettings,
  b: PersistedTemplatesSettings,
): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

export function getSessionSettings(chatId: string): PersistedTemplatesSettings | undefined {
  const map = readMap();
  const key = sessionKey(chatId);
  return key in map ? map[key].settings : undefined;
}

export function getWorkspaceDefaultSettings(
  workspaceId: string,
): PersistedTemplatesSettings | undefined {
  const map = readMap();
  const key = workspaceKey(workspaceId);
  return key in map ? map[key].settings : undefined;
}

/**
 * Persists the settings under the session key (when a chatId exists) and under the workspace's
 * last-used key. The workspace default deliberately OMITS the campaign — a campaign is content
 * specific to one email, so it is only carried within its own session; a brand-new session must not
 * inherit the previous campaign (it would risk the wrong brand/name). No-op writes are skipped by
 * callers via {@link settingsEqual}.
 */
export function saveSettings(
  settings: PersistedTemplatesSettings,
  { chatId, workspaceId }: { chatId?: string; workspaceId?: string },
): void {
  // Never persist an empty snapshot — it can only wipe good stored settings (options transiently
  // reset to nothing during navigation), never usefully save anything.
  if (Object.keys(settings).length === 0) return;
  const map = readMap();
  const updatedAt = Date.now();
  if (chatId) map[sessionKey(chatId)] = { settings, updatedAt };
  if (workspaceId) {
    const toolingOnly = { ...settings };
    delete toolingOnly.selectedCampaignId; // never carry a campaign into a brand-new session
    map[workspaceKey(workspaceId)] = { settings: toolingOnly, updatedAt };
  }
  evictOldSessions(map); // keep localStorage bounded — drop the oldest sessions past the cap
  writeMap(map);
}
